// ShellAddButton.jsx — Prominent global Add next to the appdeck logo

import React, { useCallback, useMemo, useRef } from 'react';
import { useLocation, useNavigate } from 'react-router-dom';

import { useBase } from 'context/BaseContext';
import { useCurrentLanguage } from 'context/LanguageContext';
import { useDatabase } from 'context/DatabaseContext';
import { useNavigation } from 'context/NavigationContext';
import { useNodePeek } from 'context/NodePeekContext';
import { useNodeTypes } from 'context/NodeTypesContext';
import { useTheme } from 'context/ThemeContext';
import { useUI } from 'context/UIContext';
import { useUser } from 'context/UserContext';

import useBaseAccessLock from 'hooks/useBaseAccessLock';
import useTopLevelNodeCreate from 'hooks/useTopLevelNodeCreate';

import { ADD_MENU_ACTION_KEYS } from 'components/addmenu/buildAddMenuChildren';
import { setPendingCreateNodeModule } from 'utilities/createNodeModuleContext';
import { SectionType } from 'utilities/Enums';
import { getActiveBaseKeyFromSettings } from 'utilities/baseSettingsKeys';
import { NODE_MODULE } from 'utilities/nodeModule';

import AddActionMenu from 'components/addmenu/AddActionMenu';
import Button from 'components/button/Button';
import CameraModal from 'editor/nodeadd/camera/CameraModal';

import './ShellAddButton.css';

/**
 * ShellAddButton
 *
 * Primary New control in the shell header (full-width under the base switcher).
 *
 * @returns {ReactNode}
 */
const ShellAddButton = React.memo(() => {

  // ----- CONTEXT ----- //

  const { theme } = useTheme();
  const { user, userSettings } = useUser();
  const { activeBase } = useBase();
  const { getType } = useNodeTypes();
  const language = useCurrentLanguage();
  const { setCurrentNode, setCurrentSection } = useNavigation();
  const { isSidebarRailCompact, setSelectedLocation } = useUI();
  const { setActiveDatabase } = useDatabase();
  const { openPeek } = useNodePeek();
  const compact = Boolean(isSidebarRailCompact);

  // ----- ROUTING ----- //

  const navigate = useNavigate();
  const location = useLocation();

  // ----- HOOKS ----- //

  const { accessLocked: baseNavigationLocked } = useBaseAccessLock();

  // ----- REFS ----- //

  const addButtonRef = useRef(null);

  // ----- MEMOS ----- //

  const preferredBaseKeyFromSettings = getActiveBaseKeyFromSettings(userSettings);
  const activeBaseKey = useMemo(() => {
    const key = activeBase?.key || activeBase?.id || preferredBaseKeyFromSettings || '';
    return String(key).trim();
  }, [activeBase?.id, activeBase?.key, preferredBaseKeyFromSettings]);

  // ----- HOOKS ----- //

  const {
    cameraOpen,
    cameraContentType,
    uploadInputRef,
    pendingUploadAccept,
    pendingUploadMultiple,
    handleTopLevelActionSelect,
    handleUploadFileSelect,
    setCameraOpen,
    handleCameraContentCreated,
  } = useTopLevelNodeCreate({
    user,
    activeBaseKey: activeBaseKey || null,
    getType,
    language,
    navigate,
    setCurrentNode,
    setCurrentSection,
    setSelectedLocation,
    setActiveDatabase,
    shouldNavigate: false,
  });

  // ----- HANDLERS ----- //

  const handleLockedNavigationAttempt = useCallback(() => {
    setActiveDatabase(null);
    setCurrentNode(null);
    setSelectedLocation(null);
    setCurrentSection(SectionType.HOME);
    if (location.pathname !== '/') {
      navigate('/', { replace: true });
    }
  }, [
    location.pathname,
    navigate,
    setActiveDatabase,
    setCurrentNode,
    setCurrentSection,
    setSelectedLocation,
  ]);

  const createAndPeek = useCallback(async (typeKey, options = {}) => {
    const newNode = await handleTopLevelActionSelect(typeKey, {
      module: NODE_MODULE.PRIVATE,
      shouldNavigate: false,
      provisional: true,
      initialFields: options?.initialFields || null,
    });
    if (newNode?.key) {
      openPeek(newNode, { mode: 'reset' });
    }
    return newNode;
  }, [handleTopLevelActionSelect, openPeek]);

  const handleAddTypeSelect = useCallback(async (typeKey, options = {}) => {
    if (baseNavigationLocked) {
      handleLockedNavigationAttempt();
      return;
    }

    setPendingCreateNodeModule(NODE_MODULE.PRIVATE);

    const normalizedKey = String(typeKey || '').trim().toUpperCase();
    if (
      normalizedKey === ADD_MENU_ACTION_KEYS.UPLOAD
      || normalizedKey === 'ATTACH'
      || normalizedKey === ADD_MENU_ACTION_KEYS.PHOTO
      || normalizedKey === 'CAMERA'
      || normalizedKey === 'SNAPSHOT'
      || normalizedKey === ADD_MENU_ACTION_KEYS.VIDEO
      || normalizedKey === 'RECORDING'
    ) {
      const actionKey = normalizedKey === ADD_MENU_ACTION_KEYS.PHOTO
        ? 'CAMERA'
        : normalizedKey === ADD_MENU_ACTION_KEYS.VIDEO
          ? 'RECORDING'
          : normalizedKey === ADD_MENU_ACTION_KEYS.UPLOAD
            ? 'ATTACH'
            : normalizedKey;
      handleTopLevelActionSelect(actionKey, { module: NODE_MODULE.PRIVATE });
      return;
    }

    await createAndPeek(typeKey, options);
  }, [
    baseNavigationLocked,
    createAndPeek,
    handleLockedNavigationAttempt,
    handleTopLevelActionSelect,
  ]);

  const highlightForeground = theme.highlightForeground || theme.foreground;

  // ----- RENDER ----- //

  if (!activeBaseKey) return null;

  return (
    <>
      <div
        ref={addButtonRef}
        className={[
          'shell-add-button',
          compact ? 'is-rail-compact' : '',
        ].filter(Boolean).join(' ')}
      >
        <Button
          iconKey="add"
          iconSize={compact ? 20 : 18}
          label={compact ? undefined : 'Add…'}
          title="Add…"
          enabled={!baseNavigationLocked}
          backgroundColor={theme.highlight}
          borderColor={theme.highlight}
          foregroundColor={highlightForeground}
          hoverBackgroundColor={theme.highlightHover || theme.highlight}
          hoverForegroundColor={highlightForeground}
          borderRadius="9px"
          fontSize="14px"
          gap="4px"
          height="36px"
          width={compact ? '36px' : '100%'}
          padding={compact ? '0' : '0 12px'}
        />
      </div>

      <input
        ref={uploadInputRef}
        accept={pendingUploadAccept || undefined}
        multiple={pendingUploadMultiple}
        style={{ display: 'none' }}
        type="file"
        onChange={handleUploadFileSelect}
      />

      <CameraModal
        isOpen={cameraOpen}
        setOpen={setCameraOpen}
        contentType={cameraContentType}
        onContentCreated={handleCameraContentCreated}
      />

      <AddActionMenu
        mode="kb"
        onTypeSelect={handleAddTypeSelect}
        triggerRef={addButtonRef}
      />
    </>
  );
});

ShellAddButton.displayName = 'ShellAddButton';

export default ShellAddButton;
